import { softSetProperties } from 'rise-ui/utils/object.js';

export const epsilon = Number.EPSILON * 1000;

export class PixelIndex {
  window = [ 0, 1 ];

  /**
   * @param {number} size Fixed number of buckets to use for index
   */
  constructor(size) {
    size = +size || 0;
    this.index = { length: size };
    this.clear();
    this.width = size;
    this.setDomain([ 0, size ]);
  }

  _width = 0;
  _minWin = this.window[0];
  _maxWin = this.window[1];
  _byPixel = [];

  clear() {
    const size = this.index.length;
    const index = [];
    for (let i = 0; i < size; ++i) {
      index[i] = [];
    }
    this.index = index;
    this.count = 0;
  }

  get size() {
    return this.index.length;
  }

  /**
   * Set reference to data collection
   * Note: Use `addDataIndex` must be used to process each datum into pixel index
   * @param {({})[]} data
   */
  setData(data) {
    this.data = data;
    return this;
  }

  /**
   * Set data key for value within `size` range
   * Every datum must have float property of this key within domain of `size`
   * @param {string} key
   */
  setDataKey(key) {
    this.key = key;
    return this;
  }

  /**
   * Set current low and high domain extents for indexing of new datum
   * Note: calling will result in iteration/rearranging of all data in index
   * @param {[number,number]} domain
   * @returns {PixelIndex}
   */
  setDomain(domain) {
    this.domain = domain;

    const { size } = this;

    if (this.count) {
      const { index } = this;
      this.clear();
      for (let i = 0; i < size; ++i) {
        const bucket = index[i];
        for (let b = 0, len = bucket.length; b < len; ++b) {
          this.addDataIndex(bucket[b]);
        }
      }
    }

    const [ min, max ] = this.domain;
    const dist = max - min;
    const ratio = size / dist;
    const last = size - 1;

    /**
     * Scale value within domain to value within size (i.e. get index within pixel index)
     * @param value
     * @returns {number}
     */
    this.scale = value =>
      Math.max(0, Math.min(Math.floor((value - min) * ratio), last));

    return this;
  }

  /**
   * Add datum index to pixel index
   * @param index
   */
  addDataIndex(index) {
    const { key } = this;
    const d = this.data[index];
    const i = this.scale(Math.floor(key ? d[key] : d));
    const p = this.index[i];
    p[p.length] = index;
    ++this.count;
    this.invalidatePixelByIndex(i);
    return this;
  }

  /**
   * Set pixel width of the actual represented pixel range (e.g. width of rendered graph)
   * @param {number} width
   * @returns {PixelIndex}
   */
  setWidth(width) {
    this.width = Math.ceil(width);
    return this;
  }

  /**
   * Set current low and high domain extents as values from `0.0` to `1.0`
   * @param {[number,number]} window
   * @returns {PixelIndex}
   */
  setWindow(window) {
    this.window = window;
    return this;
  }

  // TODO implement
  invalidatePixelByIndex(index) {
    let { _byPixel } = this;
    const pixel = -1; // from index
    if (pixel >= 0) {
      _byPixel[pixel] = null;
    }
  }

  invalidatePixels() {
    this._byPixel = new Array(this.width);
  }

  /**
   * Gives array of data indexes that are within the given pixel.
   * Pass null to give back an array of all pixels.
   * @param {null|number} pixel
   * @returns {*[]|*[][]}
   */
  getByPixel(pixel = null) {
    const { size, width } = this;

    // Return array of all pixels if no pixel given
    if ((pixel ?? null) === null) {
      const byPixel = new Array(width);
      // FIXME try `size` in place of `width` and fix last datum in array that represents pixel out of bounds
      for (let i = 0; i < width; ++i) {
        byPixel[i] = this.getByPixel(i);
      }
      return byPixel;
    }

    // Toss out cache if dependent variables change
    const [ minWin, maxWin ] = this.window;
    if (softSetProperties(this, {
      _width: width,
      _minWin: minWin,
      _maxWin: maxWin,
    })) {
      this.invalidatePixels();
    }

    // Return cache if available
    let { _byPixel } = this;
    if (_byPixel[pixel]) {
      return _byPixel[pixel];
    }

    const { data, index: indexes, key } = this;
    const byPixel = [];
    const [ min, max ] = this.domain;

    const dist = max - min;
    const s = maxWin - minWin;

    const last = size - 1;
    const isAtEnd = pixel === width - 1;
    const clamp = value => Math.min(Math.max(0, Math.floor(value)), last);
    const arrayUnit = s * size / width;
    const domainUnit = dist / size * arrayUnit;
    const arrayStartFloat = pixel * arrayUnit + minWin * size;
    const arrayStart = clamp(arrayStartFloat);
    const arrayEnd = clamp(arrayStartFloat + arrayUnit);
    let domainStart = min + pixel * domainUnit + minWin * dist;
    let domainEnd = domainStart + domainUnit;

    let found = false;
    arrayLoop: for (let i = arrayStart; i <= arrayEnd; ++i) {
      const { length } = indexes[i];
      for (let j = 0; j < length; ++j) {
        const idx = indexes[i][j];
        let value = data[idx][key];
        // If we already found or if this is a starting point...
        if (found || value >= domainStart) {
          // Keep discrete windows deterministically based on the calculation,
          // while using the precise domain-end on last bucket to prevent
          // losing final values cut off by floating point inaccuracies.
          if (isAtEnd ? value > domainEnd && Math.abs(value - domainEnd) > epsilon : value >= domainEnd) {
            // End iteration if value approximately >= domain end,
            // or it's the end of the current domain/window.
            i = arrayEnd;
            break arrayLoop;
          }
          found = true;
          byPixel[byPixel.length] = idx;
        }
      }
    }

    return _byPixel[pixel] = byPixel;
  }

  getPixelByDataIndex(index) {
    const { data } = this;
    const { length } = data;
    if (index < 0) {
      index += length;
    }
    if (index < 0 || index >= length) {
      return -1;
    }
    const value = this.data[index][this.key];
    const [ min, max ] = this.domain;
    return Math.min(Math.floor((value - min) / (max - min) * this.width), this.width - 1);
  }
}
